// Fleet — tabbed registry (Vehicles | Equipment). CP1 = vehicles, CP2 adds
// equipment. Tab state rides the ?tab= query param (FieldHub pattern). Each
// tab is a self-contained registry: list + status filter + expiry badges
// (30-day threshold, vendor-compliance treatment) + create/edit modal.

import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { Plus, X, Truck, Wrench, AlertTriangle, Trash2, Wrench as WrenchIcon } from 'lucide-react'
import { toast } from 'sonner'
import { scheduleDueState, worstState, type MaintenanceSchedule, type DueState } from './maintenanceUtils'

// ─── Shared bits ────────────────────────────────────────────────────────────
export const field: React.CSSProperties = { padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', width: '100%' }
export const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }
const cellStyle: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: 'var(--ink-secondary)', borderBottom: '1px solid var(--border-subtle)', textAlign: 'left', verticalAlign: 'middle' }
const thStyle: React.CSSProperties = { padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--border-default)' }

export function ExpiryBadge({ date, label }: { date: string | null; label: string }) {
  if (!date) return null
  const d = new Date(date)
  const expired = d < new Date()
  const soon = !expired && d < new Date(Date.now() + 30 * 86400000)
  if (!expired && !soon) return null
  const tone = expired ? 'var(--danger)' : 'var(--warning)'
  const bg = expired ? 'var(--danger-dim)' : 'var(--warning-dim)'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: tone, background: bg, padding: '2px 7px', borderRadius: 100, whiteSpace: 'nowrap' }}>
      <AlertTriangle style={{ width: 10, height: 10 }} /> {label} {expired ? 'expired' : 'soon'}
    </span>
  )
}

export function StatusPill({ meta }: { meta: { label: string; color: string; bg: string } }) {
  return <span style={{ fontSize: 11, fontWeight: 600, color: meta.color, background: meta.bg, padding: '2px 8px', borderRadius: 100 }}>{meta.label}</span>
}

// Maintenance chip — worst-state wins; blank when nothing's due.
export function MaintChip({ state }: { state: DueState }) {
  if (state === 'ok') return <span style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>—</span>
  const overdue = state === 'overdue'
  const tone = overdue ? 'var(--danger)' : 'var(--warning)'
  const bg = overdue ? 'var(--danger-dim)' : 'var(--warning-dim)'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: tone, background: bg, padding: '2px 7px', borderRadius: 100, whiteSpace: 'nowrap' }}>
      <WrenchIcon style={{ width: 10, height: 10 }} /> {overdue ? 'Overdue' : 'Due soon'}
    </span>
  )
}

function FilterRow<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<{ v: T; label: string }> }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
      {options.map(o => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)}
          style={{ padding: '4px 11px', borderRadius: 100, border: '1px solid', borderColor: value === o.v ? 'var(--accent)' : 'var(--border-default)', background: value === o.v ? 'var(--accent-dim)' : 'transparent', color: value === o.v ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ─── Tab shell ──────────────────────────────────────────────────────────────
type Tab = 'vehicles' | 'equipment'

export default function FleetPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: Tab = tabParam === 'equipment' ? 'equipment' : 'vehicles'
  const switchTab = (t: Tab) => setSearchParams(t === 'vehicles' ? {} : { tab: t })

  const TABS: Array<{ id: Tab; label: string; icon: typeof Truck }> = [
    { id: 'vehicles', label: 'Vehicles', icon: Truck },
    { id: 'equipment', label: 'Equipment', icon: Wrench },
  ]

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: 'var(--ink-primary)' }}>Fleet</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>Vehicles and equipment in your workspace.</p>
      </div>
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border-subtle)', marginBottom: 18 }}>
        {TABS.map(t => {
          const active = activeTab === t.id
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => switchTab(t.id)}
              style={{ padding: '10px 14px', background: 'none', border: 'none', borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`, color: active ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon style={{ width: 14, height: 14 }} /> {t.label}
            </button>
          )
        })}
      </div>
      {activeTab === 'vehicles' ? <VehiclesTab /> : <EquipmentTab />}
    </div>
  )
}

// ─── Vehicles tab (CP1) ─────────────────────────────────────────────────────
export interface VehicleRow {
  id: string; workspace_id: string; name: string; make: string | null; model: string | null; year: number | null
  vin: string | null; license_plate: string | null; registration_expires_at: string | null; insurance_expires_at: string | null
  odometer: number | null; status: string; assigned_agent_id: string | null; notes: string | null
}
export const VEHICLE_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  active:  { label: 'Active',  color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  in_shop: { label: 'In shop', color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  retired: { label: 'Retired', color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
}

function VehiclesTab() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'active' | 'in_shop' | 'retired'>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<VehicleRow | null>(null)

  const { data: vehicles = [], isLoading, error } = useQuery<VehicleRow[]>({
    queryKey: ['vehicles', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('vehicles').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) { console.error('[fleet] vehicles query failed', error); throw error }
      return (data ?? []) as VehicleRow[]
    },
    enabled: !!workspace?.id,
  })
  const { data: techs = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['fleet-techs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace.id)
      if (error) { console.error('[fleet] techs query failed', error); throw error }
      return data ?? []
    },
    enabled: !!workspace?.id,
  })
  const techName = useMemo(() => new Map(techs.map(t => [t.id, t.full_name ?? 'Unnamed'])), [techs])

  // One grouped fetch of active schedules -> per-vehicle worst maintenance state
  // (avoids a per-row query). Vehicle odometer feeds the mileage thresholds.
  const { data: schedules = [] } = useQuery<MaintenanceSchedule[]>({
    queryKey: ['fleet-vehicle-schedules', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('maintenance_schedules').select('*').eq('workspace_id', workspace.id).eq('active', true).not('vehicle_id', 'is', null)
      if (error) { console.error('[fleet] schedules query failed', error); throw error }
      return (data ?? []) as MaintenanceSchedule[]
    },
    enabled: !!workspace?.id,
  })
  const maintByVehicle = useMemo(() => {
    const now = Date.now()
    const odo = new Map(vehicles.map(v => [v.id, v.odometer]))
    const m = new Map<string, DueState>()
    for (const s of schedules) {
      if (!s.vehicle_id) continue
      const st = scheduleDueState(s, odo.get(s.vehicle_id) ?? null, now)
      m.set(s.vehicle_id, worstState([m.get(s.vehicle_id) ?? 'ok', st]))
    }
    return m
  }, [schedules, vehicles])

  const visible = filter === 'all' ? vehicles : vehicles.filter(v => v.status === filter)

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus style={{ width: 16, height: 16 }} /> Add vehicle
        </button>
      </div>
      <FilterRow value={filter} onChange={setFilter} options={[{ v: 'all', label: 'All' }, { v: 'active', label: 'Active' }, { v: 'in_shop', label: 'In shop' }, { v: 'retired', label: 'Retired' }]} />
      {error ? (
        <ErrorBox what="vehicles" />
      ) : isLoading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState icon={Truck} empty={vehicles.length === 0} noun="vehicle" onCreate={() => { setEditing(null); setModalOpen(true) }} />
      ) : (
        <TableShell head={['Vehicle', 'Make / model', 'Plate', 'Status', 'Maint.', 'Assigned', 'Odometer', 'Expiry']}>
          {visible.map(v => {
            const s = VEHICLE_STATUS[v.status] ?? VEHICLE_STATUS.active
            const mm = [v.make, v.model, v.year ? `(${v.year})` : null].filter(Boolean).join(' ')
            return (
              <tr key={v.id} onClick={() => navigate(`/app/fleet/vehicles/${v.id}`)} style={{ cursor: 'pointer' }}>
                <td style={{ ...cellStyle, fontWeight: 600, color: 'var(--ink-primary)' }}>{v.name}</td>
                <td style={cellStyle}>{mm || '—'}</td>
                <td style={cellStyle}>{v.license_plate || '—'}</td>
                <td style={cellStyle}><StatusPill meta={s} /></td>
                <td style={cellStyle}><MaintChip state={maintByVehicle.get(v.id) ?? 'ok'} /></td>
                <td style={cellStyle}>{v.assigned_agent_id ? (techName.get(v.assigned_agent_id) ?? 'Unknown') : '—'}</td>
                <td style={cellStyle}>{v.odometer != null ? v.odometer.toLocaleString() : '—'}</td>
                <td style={cellStyle}><div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                  <ExpiryBadge date={v.registration_expires_at} label="Reg" /><ExpiryBadge date={v.insurance_expires_at} label="Ins" />
                </div></td>
              </tr>
            )
          })}
        </TableShell>
      )}
      <VehicleModal open={modalOpen} onClose={() => setModalOpen(false)}
        onSaved={() => { queryClient.invalidateQueries({ queryKey: ['vehicles', workspace?.id] }); setModalOpen(false) }}
        workspaceId={workspace?.id ?? null} editing={editing} techs={techs} />
    </>
  )
}

// ─── Equipment tab (CP2) ────────────────────────────────────────────────────
export interface EquipmentRow {
  id: string; workspace_id: string; name: string; category: string | null; serial_number: string | null
  purchase_date: string | null; warranty_expires_at: string | null; status: string
  assigned_agent_id: string | null; assigned_vehicle_id: string | null; notes: string | null
}
export const EQUIP_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  active:    { label: 'Active',    color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  in_repair: { label: 'In repair', color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  retired:   { label: 'Retired',   color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
}

function EquipmentTab() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'active' | 'in_repair' | 'retired'>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EquipmentRow | null>(null)

  const { data: equipment = [], isLoading, error } = useQuery<EquipmentRow[]>({
    queryKey: ['equipment', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('equipment').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) { console.error('[fleet] equipment query failed', error); throw error }
      return (data ?? []) as EquipmentRow[]
    },
    enabled: !!workspace?.id,
  })
  const { data: techs = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['fleet-techs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace.id)
      if (error) { console.error('[fleet] techs query failed', error); throw error }
      return data ?? []
    },
    enabled: !!workspace?.id,
  })
  const { data: vehicles = [] } = useQuery<Array<{ id: string; name: string; status: string }>>({
    queryKey: ['fleet-vehicles-picker', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('vehicles').select('id, name, status').eq('workspace_id', workspace.id).order('status').order('name')
      if (error) { console.error('[fleet] vehicles picker query failed', error); throw error }
      return data ?? []
    },
    enabled: !!workspace?.id,
  })
  const techName = useMemo(() => new Map(techs.map(t => [t.id, t.full_name ?? 'Unnamed'])), [techs])
  const vehName = useMemo(() => new Map(vehicles.map(v => [v.id, v.name])), [vehicles])

  // Per-equipment worst maintenance state — mirrors maintByVehicle. Equipment
  // schedules are date-only (CHECK-enforced), so no odometer feeds the state.
  const { data: eqSchedules = [] } = useQuery<MaintenanceSchedule[]>({
    queryKey: ['fleet-equipment-schedules', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('maintenance_schedules').select('*').eq('workspace_id', workspace.id).eq('active', true).not('equipment_id', 'is', null)
      if (error) { console.error('[fleet] equipment schedules query failed', error); throw error }
      return (data ?? []) as MaintenanceSchedule[]
    },
    enabled: !!workspace?.id,
  })
  const maintByEquipment = useMemo(() => {
    const now = Date.now()
    const m = new Map<string, DueState>()
    for (const s of eqSchedules) {
      if (!s.equipment_id) continue
      const st = scheduleDueState(s, null, now)
      m.set(s.equipment_id, worstState([m.get(s.equipment_id) ?? 'ok', st]))
    }
    return m
  }, [eqSchedules])

  const visible = filter === 'all' ? equipment : equipment.filter(e => e.status === filter)

  const assignmentLabel = (e: EquipmentRow) =>
    e.assigned_agent_id ? (techName.get(e.assigned_agent_id) ?? 'Unknown')
    : e.assigned_vehicle_id ? `Vehicle · ${vehName.get(e.assigned_vehicle_id) ?? 'Unknown'}`
    : '—'

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus style={{ width: 16, height: 16 }} /> Add equipment
        </button>
      </div>
      <FilterRow value={filter} onChange={setFilter} options={[{ v: 'all', label: 'All' }, { v: 'active', label: 'Active' }, { v: 'in_repair', label: 'In repair' }, { v: 'retired', label: 'Retired' }]} />
      {error ? (
        <ErrorBox what="equipment" />
      ) : isLoading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState icon={Wrench} empty={equipment.length === 0} noun="equipment" onCreate={() => { setEditing(null); setModalOpen(true) }} />
      ) : (
        <TableShell head={['Equipment', 'Category', 'Serial', 'Status', 'Maint.', 'Assigned', 'Warranty']}>
          {visible.map(e => {
            const s = EQUIP_STATUS[e.status] ?? EQUIP_STATUS.active
            return (
              <tr key={e.id} onClick={() => navigate(`/app/fleet/equipment/${e.id}`)} style={{ cursor: 'pointer' }}>
                <td style={{ ...cellStyle, fontWeight: 600, color: 'var(--ink-primary)' }}>{e.name}</td>
                <td style={cellStyle}>{e.category || '—'}</td>
                <td style={cellStyle}>{e.serial_number || '—'}</td>
                <td style={cellStyle}><StatusPill meta={s} /></td>
                <td style={cellStyle}><MaintChip state={maintByEquipment.get(e.id) ?? 'ok'} /></td>
                <td style={cellStyle}>{assignmentLabel(e)}</td>
                <td style={cellStyle}><ExpiryBadge date={e.warranty_expires_at} label="Warranty" /></td>
              </tr>
            )
          })}
        </TableShell>
      )}
      <EquipmentModal open={modalOpen} onClose={() => setModalOpen(false)}
        onSaved={() => { queryClient.invalidateQueries({ queryKey: ['equipment', workspace?.id] }); setModalOpen(false) }}
        workspaceId={workspace?.id ?? null} editing={editing} techs={techs} vehicles={vehicles} />
    </>
  )
}

// ─── Shared list scaffolding ────────────────────────────────────────────────
function TableShell({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
        <thead><tr>{head.map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}
function Loading() { return <p style={{ color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</p> }
function ErrorBox({ what }: { what: string }) {
  return <div style={{ padding: 20, background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 12, color: 'var(--danger)', fontSize: 13 }}>Couldn’t load {what}. Please refresh.</div>
}
function EmptyState({ icon: Icon, empty, noun, onCreate }: { icon: typeof Truck; empty: boolean; noun: string; onCreate: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '56px 20px', border: '1px dashed var(--border-default)', borderRadius: 16 }}>
      <Icon style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
      <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{empty ? `No ${noun} yet` : 'None match this filter'}</p>
      <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>{empty ? `Add your first ${noun} to start tracking it.` : 'Try a different status filter.'}</p>
      {empty && <button onClick={onCreate} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Plus style={{ width: 16, height: 16 }} /> Add {noun}</button>}
    </div>
  )
}

// ─── Modal chrome ───────────────────────────────────────────────────────────
export function ModalShell({ open, onClose, title, onDelete, canSave, saving, saveLabel, onSave, children }: {
  open: boolean; onClose: () => void; title: string; onDelete?: () => void; canSave: boolean; saving: boolean; saveLabel: string; onSave: () => void; children: React.ReactNode
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 460, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{title}</h2>
              <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            {children}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
              {onDelete ? (
                <button type="button" onClick={onDelete} style={{ padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Trash2 style={{ width: 14, height: 14 }} /> Delete</button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={onClose} style={{ padding: '8px 14px', background: 'var(--border-subtle)', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                <button type="button" onClick={onSave} disabled={!canSave}
                  style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: canSave ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, opacity: canSave ? 1 : 0.5 }}>
                  {saving ? 'Saving…' : saveLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ─── VehicleModal (CP1, unchanged behavior) ─────────────────────────────────
export function VehicleModal({ open, onClose, onSaved, workspaceId, editing, techs }: {
  open: boolean; onClose: () => void; onSaved: () => void; workspaceId: string | null; editing: VehicleRow | null; techs: Array<{ id: string; full_name: string | null }>
}) {
  const [name, setName] = useState(''); const [make, setMake] = useState(''); const [model, setModel] = useState('')
  const [year, setYear] = useState(''); const [vin, setVin] = useState(''); const [plate, setPlate] = useState('')
  const [regExp, setRegExp] = useState(''); const [insExp, setInsExp] = useState(''); const [odometer, setOdometer] = useState('')
  const [status, setStatus] = useState('active'); const [assigned, setAssigned] = useState(''); const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) return
    if (editing) {
      setName(editing.name); setMake(editing.make ?? ''); setModel(editing.model ?? '')
      setYear(editing.year != null ? String(editing.year) : ''); setVin(editing.vin ?? ''); setPlate(editing.license_plate ?? '')
      setRegExp(editing.registration_expires_at ?? ''); setInsExp(editing.insurance_expires_at ?? '')
      setOdometer(editing.odometer != null ? String(editing.odometer) : ''); setStatus(editing.status); setAssigned(editing.assigned_agent_id ?? ''); setNotes(editing.notes ?? '')
    } else {
      setName(''); setMake(''); setModel(''); setYear(''); setVin(''); setPlate(''); setRegExp(''); setInsExp(''); setOdometer(''); setStatus('active'); setAssigned(''); setNotes('')
    }
  }, [open, editing])

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!name.trim()) throw new Error('Name is required')
      const payload = {
        name: name.trim(), make: make.trim() || null, model: model.trim() || null,
        year: year.trim() ? parseInt(year, 10) : null, vin: vin.trim() || null, license_plate: plate.trim() || null,
        registration_expires_at: regExp || null, insurance_expires_at: insExp || null,
        odometer: odometer.trim() ? parseInt(odometer, 10) : null, status, assigned_agent_id: assigned || null, notes: notes.trim() || null,
      }
      if (editing) { const { error } = await supabase.from('vehicles').update(payload).eq('id', editing.id); if (error) throw error }
      else { const { error } = await supabase.from('vehicles').insert({ workspace_id: workspaceId, ...payload }); if (error) throw error }
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => toast.error(e?.message || 'Failed to save vehicle'),
  })
  const del = useMutation({
    mutationFn: async () => { if (!editing) return; const { error } = await supabase.from('vehicles').delete().eq('id', editing.id); if (error) throw error },
    onSuccess: () => onSaved(),
    onError: (e: any) => {
      // Postgres 23503 = FK violation. The equipment→vehicle FK is NO ACTION,
      // so deleting a vehicle with assigned gear fails loudly — explain why.
      if (e?.code === '23503') {
        toast.error(String(e?.message ?? '').includes('equipment_assigned_vehicle_id_fkey')
          ? 'This vehicle has equipment assigned to it — reassign or unassign the equipment first.'
          : 'This vehicle is referenced by other records and can’t be deleted.')
      } else { toast.error(e?.message || 'Failed to delete vehicle') }
    },
  })

  return (
    <ModalShell open={open} onClose={onClose} title={editing ? 'Edit vehicle' : 'Add vehicle'}
      onDelete={editing ? () => { if (window.confirm('Delete this vehicle?')) del.mutate() } : undefined}
      canSave={!!name.trim() && !save.isPending} saving={save.isPending} saveLabel={editing ? 'Save' : 'Add'} onSave={() => save.mutate()}>
      <div><label style={lbl}>Name</label><input value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="Truck 2" style={{ ...field, fontWeight: 600 }} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 10 }}>
        <div><label style={lbl}>Make</label><input value={make} onChange={e => setMake(e.target.value)} placeholder="Ford" style={field} /></div>
        <div><label style={lbl}>Model</label><input value={model} onChange={e => setModel(e.target.value)} placeholder="F-150" style={field} /></div>
        <div><label style={lbl}>Year</label><input value={year} onChange={e => setYear(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" maxLength={4} placeholder="2022" style={field} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>VIN</label><input value={vin} onChange={e => setVin(e.target.value)} style={field} /></div>
        <div><label style={lbl}>License plate</label><input value={plate} onChange={e => setPlate(e.target.value)} style={field} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Registration expires</label><input type="date" value={regExp} onChange={e => setRegExp(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
        <div><label style={lbl}>Insurance expires</label><input type="date" value={insExp} onChange={e => setInsExp(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Odometer</label><input value={odometer} onChange={e => setOdometer(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="0" style={field} /></div>
        <div><label style={lbl}>Status</label><select value={status} onChange={e => setStatus(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
          <option value="active">Active</option><option value="in_shop">In shop</option><option value="retired">Retired</option>
        </select></div>
      </div>
      <div><label style={lbl}>Assigned tech</label><select value={assigned} onChange={e => setAssigned(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
        <option value="">Unassigned</option>{techs.map(t => <option key={t.id} value={t.id}>{t.full_name ?? 'Unnamed'}</option>)}
      </select></div>
      <div><label style={lbl}>Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></div>
    </ModalShell>
  )
}

// ─── EquipmentModal (CP2) ───────────────────────────────────────────────────
type AssignMode = 'none' | 'tech' | 'vehicle'
export function EquipmentModal({ open, onClose, onSaved, workspaceId, editing, techs, vehicles }: {
  open: boolean; onClose: () => void; onSaved: () => void; workspaceId: string | null; editing: EquipmentRow | null
  techs: Array<{ id: string; full_name: string | null }>; vehicles: Array<{ id: string; name: string; status: string }>
}) {
  const [name, setName] = useState(''); const [category, setCategory] = useState(''); const [serial, setSerial] = useState('')
  const [purchase, setPurchase] = useState(''); const [warranty, setWarranty] = useState(''); const [status, setStatus] = useState('active')
  const [notes, setNotes] = useState(''); const [mode, setMode] = useState<AssignMode>('none')
  const [agentId, setAgentId] = useState(''); const [vehicleId, setVehicleId] = useState('')

  useEffect(() => {
    if (!open) return
    if (editing) {
      setName(editing.name); setCategory(editing.category ?? ''); setSerial(editing.serial_number ?? '')
      setPurchase(editing.purchase_date ?? ''); setWarranty(editing.warranty_expires_at ?? ''); setStatus(editing.status); setNotes(editing.notes ?? '')
      if (editing.assigned_agent_id) { setMode('tech'); setAgentId(editing.assigned_agent_id); setVehicleId('') }
      else if (editing.assigned_vehicle_id) { setMode('vehicle'); setVehicleId(editing.assigned_vehicle_id); setAgentId('') }
      else { setMode('none'); setAgentId(''); setVehicleId('') }
    } else {
      setName(''); setCategory(''); setSerial(''); setPurchase(''); setWarranty(''); setStatus('active'); setNotes(''); setMode('none'); setAgentId(''); setVehicleId('')
    }
  }, [open, editing])

  // Switching assignment mode clears the other id so only one can persist.
  const changeMode = (m: AssignMode) => { setMode(m); if (m !== 'tech') setAgentId(''); if (m !== 'vehicle') setVehicleId('') }

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!name.trim()) throw new Error('Name is required')
      const payload = {
        name: name.trim(), category: category.trim() || null, serial_number: serial.trim() || null,
        purchase_date: purchase || null, warranty_expires_at: warranty || null, status,
        assigned_agent_id: mode === 'tech' ? (agentId || null) : null,
        assigned_vehicle_id: mode === 'vehicle' ? (vehicleId || null) : null,
        notes: notes.trim() || null,
      }
      if (editing) { const { error } = await supabase.from('equipment').update(payload).eq('id', editing.id); if (error) throw error }
      else { const { error } = await supabase.from('equipment').insert({ workspace_id: workspaceId, ...payload }); if (error) throw error }
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => toast.error(e?.message || 'Failed to save equipment'),
  })
  const del = useMutation({
    mutationFn: async () => { if (!editing) return; const { error } = await supabase.from('equipment').delete().eq('id', editing.id); if (error) throw error },
    onSuccess: () => onSaved(), onError: (e: any) => toast.error(e?.message || 'Failed to delete equipment'),
  })

  return (
    <ModalShell open={open} onClose={onClose} title={editing ? 'Edit equipment' : 'Add equipment'}
      onDelete={editing ? () => { if (window.confirm('Delete this equipment?')) del.mutate() } : undefined}
      canSave={!!name.trim() && !save.isPending} saving={save.isPending} saveLabel={editing ? 'Save' : 'Add'} onSave={() => save.mutate()}>
      <div><label style={lbl}>Name</label><input value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="Compressor" style={{ ...field, fontWeight: 600 }} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Category</label><input value={category} onChange={e => setCategory(e.target.value)} placeholder="Power tool" style={field} /></div>
        <div><label style={lbl}>Serial number</label><input value={serial} onChange={e => setSerial(e.target.value)} style={field} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Purchase date</label><input type="date" value={purchase} onChange={e => setPurchase(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
        <div><label style={lbl}>Warranty expires</label><input type="date" value={warranty} onChange={e => setWarranty(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
      </div>
      <div><label style={lbl}>Status</label><select value={status} onChange={e => setStatus(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
        <option value="active">Active</option><option value="in_repair">In repair</option><option value="retired">Retired</option>
      </select></div>
      <div>
        <label style={lbl}>Assignment</label>
        <select value={mode} onChange={e => changeMode(e.target.value as AssignMode)} style={{ ...field, cursor: 'pointer', marginBottom: mode === 'none' ? 0 : 8 }}>
          <option value="none">Unassigned</option><option value="tech">Tech</option><option value="vehicle">Vehicle</option>
        </select>
        {mode === 'tech' && (
          <select value={agentId} onChange={e => setAgentId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
            <option value="">Select a tech…</option>{techs.map(t => <option key={t.id} value={t.id}>{t.full_name ?? 'Unnamed'}</option>)}
          </select>
        )}
        {mode === 'vehicle' && (
          <select value={vehicleId} onChange={e => setVehicleId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
            <option value="">Select a vehicle…</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.name}{v.status !== 'active' ? ` (${v.status})` : ''}</option>)}
          </select>
        )}
      </div>
      <div><label style={lbl}>Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></div>
    </ModalShell>
  )
}
